import { CurrencyCode } from 'helpers/currency';
import { ContentTypes, getResponseDefault } from '../helpers/api';
import { GameType } from '../helpers/enums';
import { factory } from './base';
import { DEMO_CURRENCY } from 'helpers/constants';
import { snakify } from '../helpers';

export interface GameCategory {
  id: string;
  title: string;
  gamesCount?: number;
}

export enum Category {
  TOP = 'top',
  NEW = 'new',
  WAGERING = 'bonus_wagering',
}

export interface GameD {
  title: string;
  provider: string;
  collections: {
    [providerId: string]: number;
  };
  real: Real;
  demo?: string;
}

export type Real = {
  [currencyCode: string]: {
    id: number;
    jackpot: number;
  };
};

export type GamePackObject = {
  [id: string]: GameD;
};

export type Game = GameD & {
  id: string;
  fullPath: string;
  seoPath: string;
  listCollections: string[];
};

export interface Provider {
  id: string;
  title: string;
  gamesCount: number;
}

export const gameCategories = factory((r) => (lng: string) =>
  r<GameCategory[]>('get', `games/collections?l=${lng}`)
);

export const providers = factory((r) => () =>
  r<Provider[]>('get', `games/providers`)
);

// use default transformResponse
export const games = factory((r) => (type: GameType) =>
  r<GamePackObject>('get', `games/${type}`, undefined, {
    transformResponse: [getResponseDefault()],
  })
);

export const setFavoriteGame = factory((r) => (currencyCode: number) =>
  r<null>('put', `player/favorite_games/${currencyCode}`)
);

export const deleteFavoriteGame = factory((r) => (currencyCode: number) =>
  r<null>('delete', `player/favorite_games/${currencyCode}`)
);

export enum GamesFilterCategoriesStrategy {
  OR = 'OR',
  AND = 'AND',
}

export enum GamesFilterSortType {
  GLOBAL = 'global',
  NOVELTY = 'novelty',
  POPULARITY = 'popularity',
}

export enum GamesFilterSortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export namespace GamesFilter {
  export interface Sort {
    type: GamesFilterSortType;
    direction: GamesFilterSortDirection;
    topCategories?: string[];
  }
  export interface Body {
    device?: Device;
    currencies: CurrencyCode[] | typeof DEMO_CURRENCY[];
    pageSize?: number; // MIN 1 MAX 100
    page?: number;
    withoutTerritorialRestrictions?: boolean;
    filter?: Filter;
    sort?: Sort;
  }

  export interface BodySelectBySEOTitles {
    gameSeoTitles: string[];
    device?: Device;
  }

  export interface BodySelect {
    gameIds: string[];
    device?: Device;
  }

  export interface ResponseSelect {
    [gameId: string]: GameV2.Game;
  }

  export type Device = 'desktop' | 'mobile';

  export interface Filter {
    currencies?: CurrencyCode[];
    providers?: string[];
    volatilityRating?: VolatilityRating[];
    lines?: number;
    ways?: number;
    hitRate?: number;
    payout?: number;
    title?: string;
    categories?: Categories;
  }

  export type VolatilityRating =
    | 'low'
    | 'low-medium'
    | 'medium'
    | 'medium-high'
    | 'high'
    | 'very-high';

  export interface Categories {
    identifiers: string[];
    strategy?: GamesFilterCategoriesStrategy;
  }

  export interface Response {
    data: GameV2.Game[];
    pagination: Pagination;
  }

  export interface ResponseV2 {
    data: Game[];
    pagination: Pagination;
  }

  export interface Pagination {
    currentPage: number;
    nextPage: number;
    prevPage: number | null;
    totalPages: number;
    totalCount: number;
  }
}

export namespace GameV2 {
  export interface Game {
    title: string;
    provider: string;
    seoTitle: string;
    identifier: string;
    lines: number;
    ways: number | null;
    volatilityRating: GamesFilter.VolatilityRating;
    hitRate: string | null;
    payout: string;
    currencies: Currencies;
    unfinishedGamesFor: string[] | null;
    categories: string[];
    devices: GamesFilter.Device[];
    uniqSeoTitle: boolean;
  }

  export interface Currencies {
    [currencyCode: string]: Currency;
  }

  export interface Currency {
    id: number;
    jackpot: number;
  }
}

export const gamesFilterV2 = factory((r) => (body?: GamesFilter.Body) =>
  r<GamesFilter.Response>('post', 'games_filter', snakify({ ...body }), {
    headers: { accept: ContentTypes.vndJsonV2 },
  })
);

export const gamesFilterSelect = factory(
  (r) => (body: GamesFilter.BodySelect) =>
    r<GamesFilter.ResponseSelect>(
      'post',
      'games_filter/select',
      snakify({ ...body }),
      { headers: { accept: ContentTypes.vndJsonV2 } }
    )
);

export const gamesFilterSelectBySEOTitle = factory(
  (r) => (body: GamesFilter.BodySelectBySEOTitles) =>
    r<GamesFilter.ResponseSelect>(
      'post',
      'games_filter/select_by_seo_titles',
      snakify({ ...body }),
      { headers: { accept: ContentTypes.vndJsonV2 } }
    )
);

export const gamesFilterByJackpotCategory = factory(
  (r) => (jackpotCategoryId: string) =>
    r<GamesFilter.ResponseSelect>(
      'post',
      'games_filter/select_by_jackpot_category',
      snakify({ jackpotCategoryId }),
      { headers: { accept: ContentTypes.vndJsonV2 } }
    )
);

export interface RecentGame {
  identifier: string;
  lastActivityAt: string;
}

export interface RecentGame {
  identifier: string;
  lastActivityAt: string;
}

export const recentGames = factory((r) => () =>
  r<RecentGame[]>('get', 'player/played_games')
);

export const favoriteGames = factory((r) => () =>
  r<GameV2.Game[]>('get', 'player/favorite_games', undefined, {
    headers: { accept: ContentTypes.vndJsonV2 },
  })
);

export const favoriteGamesIds = factory((r) => () =>
  r<number[]>('get', 'player/favorite_games')
);

export const addFavoriteGame = factory((r) => (currencyCode: number) =>
  r<null>('put', `player/favorite_games/${currencyCode}`)
);
